import { useState, useEffect, useRef, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  Paper,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Fab,
  Tooltip
} from '@mui/material';
import {
  Refresh,
  Edit,
  DirectionsCar,
  Assignment,
  Save,
  Cancel,
  Add,
  Navigation,
  KeyboardArrowUp,
  Launch,
  Delete,
  LockOpen
} from '@mui/icons-material';
import TaskDetailsModal from '../../components/TaskDetailsModal/TaskDetailsModal';
import TrailerMoveModal from '../../components/TrailerMoveModal';
import AddTrailerModal from '../../components/AddTrailerModal';
import { DockDoor, YardSpot, loadAvailableLocations } from '../../api/availableLocations';
import { ReportButton } from '../../components/ReportButton';
import {
  saveFacilityToStorage,
  loadFacilityFromStorage,
  formatFacilityOptions
} from '../../utils/facilityStorage';
import { detectAndroidDevice } from '../../utils/deviceDetection';
import { UserContext } from '../../App';

import { getAuthToken } from '../../utils/cookieUtils';
import { isAnySpotter } from '../../utils/roleHelper';
import { REPORTS_WEBSOCKET_URL } from '../../constants';
import CampusFacilityMultiSelect from 'components/Campus/CampusFacilityMultiSelect';
import FacilityBadge from 'components/Campus/FacilityBadge';
import TrailerNotesEditor from './TrailerNotesEditor';
import TrailerSearchField from './TrailerSearchField';
import { useCampus } from 'hooks/useCampus';
import { useCampusDestinations } from 'hooks/useCampusDestinations';
import { useCreatedByFacility } from 'hooks/useCreatedByFacility';
import { campusFromDestinationFacilities, withCreatedByFacility } from 'utils/createdByFacility';
import { zoneColor } from 'utils/zoneStyle';
import { useCampusFacilitySelection } from 'hooks/useCampusFacilitySelection';
import { facilityDisplayName, formatCampusLocation } from 'utils/campusLabels';
import { lineageCheckIn } from 'utils/lineage';
import { useShipmentChoice } from 'hooks/useShipmentChoice';
// GraphQL API URL
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Interface for facility
interface Facility {
  id: number;
  name: string;
  facility_alias?: string | null;
}

// Interface for trailer
interface Trailer {
  uuid: string;
  trailer_number: string;
  /** The campus facility the trailer is at. */
  facility?: {
    id: string;
    name: string;
    facility_alias?: string | null;
    configuration?: any;
  };
  carrier?: {
    name: string;
  };
  current_location?: {
    __typename?: string;
    uuid?: string;
    name: string;
    facility_zone?: {
      uuid: string;
      name: string;
    };
  };
  active_lineage?: {
    uuid: string;
    notes?: string;
    created_at?: string;
    trailer_state?: {
      shipment?: {
        reference_number: string;
      };
    };
  };
  status?: {
    name: string;
  };
  data?: {
    oos?: boolean;
    [key: string]: any;
  };
  isLocked?: boolean;
}

const Trailers = () => {
  const theme = useTheme();
  // @ts-ignore
  const { user } = useContext(UserContext);

  // Check if we're in android mode based on device detection or user role
  const isAndroidDevice = detectAndroidDevice();
  const isSpotterRole = isAnySpotter(user);
  const isMobile = isAndroidDevice || isSpotterRole;
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('lg'));
  
  // State for facilities
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [selectedFacility, setSelectedFacilityState] = useState<Facility | null>(() => {
    // Initialize from localStorage on mount
    return loadFacilityFromStorage();
  });

  // Wrapper function to save facility selection to localStorage
  const setSelectedFacility = (facility: Facility | null) => {
    setSelectedFacilityState(facility);
    saveFacilityToStorage(facility);
  };
  
  // The campus of the selected facility, and which of its facilities to show
  const {
    campus,
    campusFacilities,
    isMultiFacility,
    loading: isLoadingCampus,
  } = useCampus(selectedFacility);
  const [selectedCampusFacilityIds, setSelectedCampusFacilityIds] = useCampusFacilitySelection(
    campus,
    campusFacilities,
    { startWithAll: new URLSearchParams(window.location.search).get('facilities') === 'all' },
  );

  // State for trailers
  const [trailers, setTrailers] = useState<Trailer[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Edit trailer modal states
  const [isEditTrailerModalOpen, setIsEditTrailerModalOpen] = useState<boolean>(false);
  const [selectedTrailer, setSelectedTrailer] = useState<Trailer | null>(null);
  const [newTrailerNumber, setNewTrailerNumber] = useState<string>('');
  const [newScacValue, setNewScacValue] = useState<string>('');
  const [newStatusLoaded, setNewStatusLoaded] = useState<boolean>(false);
  const [newOutOfService, setNewOutOfService] = useState<boolean>(false);
  const [originalOutOfService, setOriginalOutOfService] = useState<boolean>(false);
  const [newShipmentReference, setNewShipmentReference] = useState<string>('');
  const [shipmentReferenceError, setShipmentReferenceError] = useState<string>('');
  // Set once the user edits the reference, so saving other fields never re-sends it.
  const [shipmentReferenceTouched, setShipmentReferenceTouched] = useState<boolean>(false);
  const [isUpdatingTrailer, setIsUpdatingTrailer] = useState<boolean>(false);
  const { chooseShipment, shipmentChoiceDialog } = useShipmentChoice();
  const { chooseCreatedByFacility, createdByFacilityDialog } = useCreatedByFacility();

  // Trailer notes states
  const [isEditingTrailerNotes, setIsEditingTrailerNotes] = useState<boolean>(false);
  const [savingTrailerNotes, setSavingTrailerNotes] = useState<boolean>(false);

  // Move task modal states
  const [isMoveTaskModalOpen, setIsMoveTaskModalOpen] = useState<boolean>(false);
  const [availableDockDoors, setAvailableDockDoors] = useState<DockDoor[]>([]);
  const [availableYardSpots, setAvailableYardSpots] = useState<YardSpot[]>([]);
  const [availableFacilityZones, setAvailableFacilityZones] = useState<any[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState<boolean>(false);

  // Add inspection task modal states
  const [isAddInspectionModalOpen, setIsAddInspectionModalOpen] = useState<boolean>(false);
  const [isCreatingInspectionTask, setIsCreatingInspectionTask] = useState<boolean>(false);

  // Add Trailer Modal state
  const [isAddTrailerModalOpen, setIsAddTrailerModalOpen] = useState<boolean>(false);

  // Success modal states
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [createdTask, setCreatedTask] = useState<any>(null);

  // Add Trailer Success modal states
  const [showAddTrailerSuccessModal, setShowAddTrailerSuccessModal] = useState<boolean>(false);
  const [addTrailerSuccessMessage, setAddTrailerSuccessMessage] = useState<string>('');

  // Task details modal states
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState<boolean>(false);
  const [selectedTaskForDetails, setSelectedTaskForDetails] = useState<any>(null);

  // Lock-based state
  const [lockedTrailers, setLockedTrailers] = useState<Set<string>>(new Set());
  const [activeLocks, setActiveLocks] = useState<any[]>([]);
  const [clearingLock, setClearingLock] = useState<boolean>(false);

  // Navigation states  
  const [showNavigation, setShowNavigation] = useState<boolean>(false);
  const groupRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({});

  // State for search — initialize from URL query param if present
  const [searchTerm, setSearchTerm] = useState<string>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('q') || '';
  });

  // State for scroll position and jump to top button
  const [showJumpToTop, setShowJumpToTop] = useState<boolean>(false);

  // State for remove shipment confirmation
  const [showRemoveShipmentConfirm, setShowRemoveShipmentConfirm] = useState<boolean>(false);
  const [isRemovingShipment, setIsRemovingShipment] = useState<boolean>(false);

  // Fetch facilities using GraphQL
  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // GraphQL query for facilities
        const facilitiesQuery = `
          query {
            facilities {
              id
              name
              facility_alias
            }
          }
        `;

        // Get auth token from cookie
        const authToken = getAuthToken();

        // Fetch facilities using GraphQL
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: facilitiesQuery
          })
        });

        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }

        setFacilities(result.data.facilities);
        
        // Validate localStorage facility still exists, otherwise set default
        const storedFacility = loadFacilityFromStorage();
        if (storedFacility) {
          const facilityExists = result.data.facilities.find((f: any) => f.id === storedFacility.id);
          if (!facilityExists) {
            // Stored facility no longer exists, set default
            if (result.data.facilities.length > 0) {
              setSelectedFacility(result.data.facilities[0]);
            }
          }
          // If facility exists, keep the localStorage value (already set in useState initializer)
        } else {
          // No stored facility, set default
          if (result.data.facilities.length > 0) {
            setSelectedFacility(result.data.facilities[0]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch facilities:', err);
        setError('Failed to fetch facilities');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFacilities();
  }, []);

  // Fetch trailers using GraphQL
  const fetchTrailersData = async () => {
    if (!selectedFacility || isLoadingCampus) return;
    
    try {
      setIsLoadingData(true);
      setError(null);

      // Get auth token from cookie
      const authToken = getAuthToken();

      // GraphQL query for trailers in facility and facility locks
      const trailersQuery = `
        query GetTrailersAndLocks($facilityId: ID!, $facilityIds: [ID!]) {
          trailers_in_campus(facility_id: $facilityId, facility_ids: $facilityIds) {
            uuid
            trailer_number
            facility {
              id
              name
              facility_alias
              configuration
            }
            carrier {
              name
            }
            active_lineage {
              uuid
              notes
              created_at
              check_in_time
              trailer_state {
                shipment {
                  reference_number
                }
              }
            }
            status {
             name
            }
            data
            current_location {
              ... on YardSpotObject {
                __typename
                uuid
                name
                facility_zone {
                  name
                  uuid
                }
              }
              ... on FacilityZoneObject {
                __typename
                uuid
                name
              }
              ... on DockObject {
                __typename
                uuid
                name
              }
            }
          }
          facilityLocks(facilityId: $facilityId) {
            reason
            resource_uuid
            model_name
            operation_type
            created_by
            task_uuid
            created_at
            expires_at
            namespace
            facility_id
            resource {
              ... on YardSpotObject {
                uuid
                name
              }
              ... on DockObject {
                uuid
                name
              }
              ... on Trailer {
                uuid
                trailerNumber
              }
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: trailersQuery,
          variables: {
            facilityId: selectedFacility?.id?.toString() || "1",
            facilityIds: selectedCampusFacilityIds
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Process trailers
      const trailersData = result.data.trailers_in_campus || [];

      // Process facility locks to determine locked trailers
      const locks = result.data.facilityLocks || [];
      const lockedTrailerIds = new Set<string>();
      const activeLocksList: any[] = [];

      locks.forEach((lock: any) => {
        // Check if this lock applies to a trailer
        const isTrailerLock = lock.model_name === 'Trailer' || 
                             (lock.resource && lock.resource.__typename === 'Trailer');
        
        // Extra check for yard check operations specifically
        const isYardCheckLock = lock.operation_type === 'yard_check' || 
                               lock.reason?.includes('yard_check') ||
                               lock.reason?.includes('inspection');
        
        if (isTrailerLock || isYardCheckLock) {
          // Store the lock information for linking to tasks
          activeLocksList.push(lock);
          
          // Add the trailer UUID to locked trailers
          const trailerUuid = lock.resource_uuid || lock.resource?.uuid;
          if (trailerUuid) {
            lockedTrailerIds.add(trailerUuid);
            
            // Debug logging for yard check locks
            if (isYardCheckLock) {
              console.log('Yard check lock detected:', {
                trailerUuid,
                operation_type: lock.operation_type,
                reason: lock.reason,
                model_name: lock.model_name,
                resource_type: lock.resource?.__typename
              });
            }
          }
        }
      });

      // Mark trailers as locked
      const trailersWithLockStatus = trailersData.map((trailer: any) => ({
        ...trailer,
        isLocked: lockedTrailerIds.has(trailer.uuid)
      }));

      setTrailers(trailersWithLockStatus);
      setLockedTrailers(lockedTrailerIds);
      setActiveLocks(activeLocksList);

    } catch (err) {
      console.error('Failed to fetch trailers:', err);
      setError('Failed to fetch trailers data');
    } finally {
      setIsLoadingData(false);
    }
  };

  // A facility change loads its campus before its trailers; the table waits on both.
  const isLoadingTable = isLoadingCampus || isLoadingData;

  // Fetch data when the facility, its campus, or the campus facility selection changes
  const selectedCampusFacilityKey = selectedCampusFacilityIds.join(',');
  useEffect(() => {
    if (selectedFacility && !isLoadingCampus) {
      fetchTrailersData();
    }
    // fetchTrailersData reads the current state; the key stands in for the id array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFacility, isLoadingCampus, selectedCampusFacilityKey]);

  // Scroll detection for jump to top button
  useEffect(() => {
    const handleScroll = () => {
      // Show jump to top button when scrolled down more than 300px
      setShowJumpToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle facility change
  const handleFacilityChange = (event: any) => {
    const facilityId = event.target.value;
    const facility = facilities.find(f => f.id === facilityId);
    if (facility) {
      setSelectedFacility(facility);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    if (selectedFacility) {
      fetchTrailersData();
    }
  };

  // Handle edit trailer
  const handleEditTrailer = (trailer: Trailer) => {
    console.log('Editing trailer:', trailer);
    console.log('Trailer active_lineage:', trailer.active_lineage);
    console.log('Trailer notes:', trailer.active_lineage?.notes);
    
    const originalOosValue = trailer.data?.oos === true;

    setSelectedTrailer(trailer);
    setNewTrailerNumber(trailer.trailer_number);
    setNewScacValue(trailer.carrier?.name || '');
    setNewStatusLoaded(trailer.status?.name?.toUpperCase() === 'LOADED');
    setNewOutOfService(originalOosValue);
    setOriginalOutOfService(originalOosValue);
    setNewShipmentReference(trailer.active_lineage?.trailer_state?.shipment?.reference_number || '');
    setShipmentReferenceError(''); // Clear any previous error
    setShipmentReferenceTouched(false);
    setIsEditingTrailerNotes(false);
    setIsEditTrailerModalOpen(true);
  };

  // Resolve the reference to one shipment id, asking which one is meant when it matches
  // more than one in the campus (the legs of an intra-campus transfer). Null means the
  // user closed that prompt.
  const validateShipmentReference = async (referenceNumber: string): Promise<string | null> => {
    if (!referenceNumber.trim()) return null;

    const choice = await chooseShipment(
      referenceNumber,
      selectedTrailer?.facility?.id || selectedFacility?.id || "1",
      selectedTrailer?.uuid || ''
    );

    if (choice.status === 'cancelled') return null;
    if (choice.status === 'none') {
      throw new Error(`No shipment found with reference number "${referenceNumber}". Please verify the reference number.`);
    }
    return choice.shipment.id;
  };

  // Handle update trailer
  const handleUpdateTrailer = async () => {
    if (!selectedTrailer) return;
    
    try {
      setIsUpdatingTrailer(true);
      
      // Only an edited reference is sent, as the chosen shipment's id, so the save never
      // re-resolves the trailer's current shipment to another leg.
      let shipmentInput: { shipment_id?: string | null } = {};
      if (shipmentReferenceTouched && newShipmentReference.trim()) {
        try {
          const shipmentId = await validateShipmentReference(newShipmentReference);
          if (!shipmentId) {
            setIsUpdatingTrailer(false);
            return;
          }
          shipmentInput = { shipment_id: shipmentId };
          setShipmentReferenceError(''); // Clear any previous error
        } catch (validationError: any) {
          setShipmentReferenceError(validationError.message);
          setIsUpdatingTrailer(false);
          return;
        }
      } else {
        if (shipmentReferenceTouched) shipmentInput = { shipment_id: null };
        setShipmentReferenceError(''); // Clear error if no reference number
      }
      
      // Get auth token from cookie
      const authToken = getAuthToken();

      const updateTrailerMutation = `
        mutation UpdateTrailer($trailerUuid: String!, $input: TrailerUpdateInput!) {
          updateTrailer(uuid: $trailerUuid, input: $input) {
            uuid
            trailer_number
            carrier {
              name
            }
            data
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: updateTrailerMutation,
          variables: {
            trailerUuid: selectedTrailer.uuid,
            input: {
              trailer_number: newTrailerNumber.trim(),
              carrier_name: newScacValue.trim(),
              ...shipmentInput,
              data: JSON.stringify({
                ...selectedTrailer.data,
                oos: newOutOfService || null
              })
            }
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Check if status needs to be updated. IN USE is an empty with a shipment
      // attached, so it already counts as "not loaded"; the backend stores
      // EMPTY as IN USE whenever a shipment is attached.
      const currentStatus = selectedTrailer.status?.name?.toUpperCase();
      const currentlyLoaded = currentStatus === 'LOADED';
      const desiredStatus = newStatusLoaded ? 'LOADED' : 'EMPTY';

      if (selectedTrailer.active_lineage?.uuid && currentlyLoaded !== newStatusLoaded) {
        // Update status using the separate mutation
        const setTrailerStatusMutation = `
          mutation SetTrailerStatus($input: SetTrailerStatusInput!) {
            setTrailerStatus(input: $input) {
              uuid
              isActive
              trailerUuid
              facilityId
              maxDepthUuid
              currentDockUuid
              currentYardSpotUuid
              trailerState {
                uuid
                trailerStatusUuid
                shipmentId
                sealNumber
              }
            }
          }
        `;

        const statusResponse = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: setTrailerStatusMutation,
            variables: {
              input: {
                trailer_lineage_uuid: selectedTrailer.active_lineage.uuid,
                targetStatusName: desiredStatus
              }
            }
          })
        });

        const statusResult = await statusResponse.json();
        
        if (statusResult.errors) {
          throw new Error(`Failed to update status: ${statusResult.errors[0].message}`);
        }
      }

      // Close modal and refresh data
      setIsEditTrailerModalOpen(false);
      await fetchTrailersData();
      
      // Note: For trailer updates, we don't show the success modal since it's not a task creation
      
    } catch (error) {
      console.error('Failed to update trailer:', error);
      setError('Failed to update trailer');
    } finally {
      setIsUpdatingTrailer(false);
    }
  };


  // Helper function to determine if remove shipment button should be enabled
  const isRemoveShipmentButtonEnabled = (trailer: Trailer | null) => {
    if (!trailer) return false;
    
    // Check if trailer has a shipment relationship
    if (!trailer.active_lineage?.trailer_state?.shipment?.reference_number) return false;
    
    // Check location - if in dock (any status), button is disabled
    if (trailer.current_location?.__typename === 'DockObject') return false;
    
    // Check status - if loaded, button is disabled
    if (trailer.status?.name?.toUpperCase() === 'LOADED') return false;
    
    // Otherwise, if in yard, unknown location, or other non-dock location and not loaded, enable button
    return true;
  };

  // Function to remove shipment relationship
  const handleRemoveShipmentRelationship = async () => {
    if (!selectedTrailer) return;
    
    try {
      setIsRemovingShipment(true);
      
      // Get auth token from cookie
      const authToken = getAuthToken();

      const removeShipmentMutation = `
        mutation RemoveShipmentFromTrailer($uuid: String!, $input: TrailerUpdateInput!) {
          updateTrailer(uuid: $uuid, input: $input) {
            uuid
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: removeShipmentMutation,
          variables: {
            uuid: selectedTrailer.uuid,
            input: {
              shipmentId: null
            }
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Close confirmation modal and refresh data
      setShowRemoveShipmentConfirm(false);
      await fetchTrailersData();
      
      // Update the selected trailer to reflect the removed shipment relationship
      if (selectedTrailer?.active_lineage?.trailer_state) {
        setSelectedTrailer({
          ...selectedTrailer,
          active_lineage: {
            ...selectedTrailer.active_lineage,
            trailer_state: {
              ...selectedTrailer.active_lineage.trailer_state,
              shipment: undefined
            }
          }
        });
      }
      
    } catch (error) {
      console.error('Failed to remove shipment relationship:', error);
      setError('Failed to remove shipment relationship');
    } finally {
      setIsRemovingShipment(false);
    }
  };

  // Function to save trailer lineage notes
  const handleSaveTrailerNotes = async (notes: string) => {
    if (!selectedTrailer) return;
    
    setSavingTrailerNotes(true);
    
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // GraphQL mutation to update trailer notes
      const updateTrailerMutation = `
        mutation UpdateTrailer($uuid: String!, $input: TrailerUpdateInput!) {
          updateTrailer(uuid: $uuid, input: $input) {
            uuid
            trailer_number
            active_lineage {
              uuid
              notes
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: updateTrailerMutation,
          variables: {
            uuid: selectedTrailer.uuid,
            input: {
              notes
            }
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Update the selected trailer with the new notes immediately for UI feedback
      if (selectedTrailer.active_lineage) {
        selectedTrailer.active_lineage.notes = notes;
      } else {
        selectedTrailer.active_lineage = {
          uuid: result.data.updateTrailer.active_lineage?.uuid || '',
          notes
        };
      }
      
      // Exit edit mode
      setIsEditingTrailerNotes(false);
      
      // Refresh data to ensure consistency
      await fetchTrailersData();
      
    } catch (error: any) {
      console.error('Failed to save trailer notes:', error);
      setError('Failed to save trailer notes. Please try again.');
    } finally {
      setSavingTrailerNotes(false);
    }
  };

  // Fetch available locations for move task
  const fetchAvailableLocations = async () => {
    if (!selectedFacility) return;

    try {
      setIsLoadingLocations(true);
      const { dockDoors, yardSpots, facilityZones } = await loadAvailableLocations(
        selectedFacility?.id?.toString() || "1"
      );
      setAvailableDockDoors(dockDoors);
      setAvailableYardSpots(yardSpots);
      setAvailableFacilityZones(facilityZones);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
      setError('Failed to fetch available locations');
    } finally {
      setIsLoadingLocations(false);
    }
  };

  // Move destinations: the campus of the trailer's own facility (not necessarily the selected one).
  const moveSourceFacilityId = selectedTrailer?.facility?.id || selectedFacility?.id?.toString() || '';
  const campusDestinations = useCampusDestinations(
    moveSourceFacilityId,
    isMoveTaskModalOpen && selectedTrailer !== null,
  );

  // Handle move task
  const handleMoveTask = (trailer: Trailer) => {
    setSelectedTrailer(trailer);
    setIsMoveTaskModalOpen(true);
  };

  // Handle successful move task creation from shared component
  const handleMoveTaskSuccess = (taskData: any) => {
    console.log('Movement task created successfully:', taskData);
    
    // Close modal and show success modal with task details
    setIsMoveTaskModalOpen(false);
    setCreatedTask(taskData);
    setShowSuccessModal(true);
    
    // Refresh trailers data to update lock status
    setTimeout(() => {
      fetchTrailersData();
    }, 500);
  };

  // Handle move task creation errors from shared component
  const handleMoveTaskError = (error: string) => {
    console.error('Move task creation failed:', error);
    setError(error);
  };


  // Handle add inspection task
  const handleAddInspectionTask = (trailer: Trailer) => {
    setSelectedTrailer(trailer);
    setIsAddInspectionModalOpen(true);
  };

  // Handle create inspection task
  const handleCreateInspectionTask = async () => {
    if (!selectedTrailer) return;
    
    try {
      setIsCreatingInspectionTask(true);

      // The trailer's own facility: in the campus roll-up it may be a sibling
      const trailerFacilityId = selectedTrailer.facility?.id || selectedFacility?.id?.toString() || "1";
      const createdBy = await chooseCreatedByFacility({
        ...campusFromDestinationFacilities(campusDestinations.facilities),
        preferredFacilityIds: [trailerFacilityId],
        noun: 'task',
        summary: `Inspection · Trailer ${selectedTrailer.trailer_number || ''}`.trim()
      });
      if (createdBy.status === 'cancelled') return;
      
      // Get auth token from cookie
      const authToken = getAuthToken();

      const createMovementTaskMutation = `
        mutation CreateMovementTask(
          $trailer_uuid: String!,
          $movement_type: String!,
          $facility_id: ID!,
          $created_by_facility_id: ID,
          $auto_complete: Boolean,
          $force: Boolean
        ) {
          create_movement_task(input: {
            trailer_uuid: $trailer_uuid
            movement_type: $movement_type
            facility_id: $facility_id
            created_by_facility_id: $created_by_facility_id
            auto_complete: $auto_complete
            force: $force
          }) {
            uuid
            data
            task_type {
              uuid
              type
            }
            task_status {
              uuid
              status
            }
            from_location
            to_location
            trailer {
              uuid
              trailer_number
            }
          }
        }
      `;

      const variables = withCreatedByFacility(
        {
          trailer_uuid: selectedTrailer.uuid,
          movement_type: "yard_check",
          facility_id: trailerFacilityId,
          auto_complete: false,
          force: false
        },
        createdBy.facilityId
      );

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: createMovementTaskMutation,
          variables
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      if (!result.data?.create_movement_task?.uuid) {
        throw new Error('Failed to create movement task');
      }

      console.log('Inspection task created successfully:', result.data.create_movement_task);
      
      // Close modal and show success modal with task details
      setIsAddInspectionModalOpen(false);
      setCreatedTask(result.data.create_movement_task);
      setShowSuccessModal(true);
      
      // Immediately refresh trailers data to update lock status
      fetchTrailersData();
      
      // Also refresh again after a short delay to catch any locks that might take a moment to propagate
      setTimeout(() => {
        fetchTrailersData();
      }, 500);
      
    } catch (error) {
      console.error('Failed to create inspection task:', error);
      setError('Failed to create inspection task');
    } finally {
      setIsCreatingInspectionTask(false);
    }
  };

  // Get location display name
  const getLocationDisplayName = (location: DockDoor | YardSpot, type: string) => {
    if (type === 'dock') {
      const dock = location as DockDoor;
      // Check configuration status first
      let status = '';
      if (dock.configuration?.status === 'unavailable') {
        status = '(Unavailable)';
      } else if (dock.trailer_name) {
        status = '(Occupied)';
      } else {
        status = '(Available)';
      }
      const lockedStatus = dock.isLocked ? ' [LOCKED]' : '';
      return `${dock.dockName} ${status}${lockedStatus}`;
    } else {
      const spot = location as YardSpot;
      // Check configuration status first
      let status = '';
      if (spot.configuration?.status === 'unavailable') {
        status = '(Unavailable)';
      } else if (spot.trailer_name) {
        status = '(Occupied)';
      } else {
        status = '(Available)';
      }
      const lockedStatus = spot.isLocked ? ' [LOCKED]' : '';
      return `${spot.yard_spot_name} ${status}${lockedStatus}`;
    }
  };

  // Format current location
  const formatCurrentLocation = (location?: { __typename?: string; name: string }) => {
    if (!location) return 'Unknown';
    const locationType = location.__typename?.replace('Object', '') || 'Unknown';
    return `${locationType}: ${location.name}`;
  };

  // More than one facility on screen: labels and groups carry the facility.
  const showFacility = isMultiFacility && selectedCampusFacilityIds.length > 1;

  // Format check-in time
  const formatCheckInTime = (created_at?: string) => {
    if (!created_at) return 'N/A';
    try {
      const date = new Date(created_at);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Group trailers by location type and facility zone using UUID
  const groupTrailersByLocation = (trailers: Trailer[]) => {
    const groups: {
      [locationType: string]: {
        [facilityZoneKey: string]: {
          facilityZoneName: string;
          trailers: Trailer[];
        }
      }
    } = {};

    trailers.forEach(trailer => {
      const location = trailer.current_location;
      
      // Handle trailers without current_location
      if (!location) {
        const locationType = 'Unknown';
        const facilityZoneKey = 'unknown';
        const facilityZoneName = 'Unknown';

        if (!groups[locationType]) {
          groups[locationType] = {};
        }
        if (!groups[locationType][facilityZoneKey]) {
          groups[locationType][facilityZoneKey] = {
            facilityZoneName,
            trailers: []
          };
        }
        
        groups[locationType][facilityZoneKey].trailers.push(trailer);
        return;
      }

      const locationType = location.__typename || 'Unknown';
      let facilityZoneKey = 'unknown';
      let facilityZoneName = 'Unknown Zone';
      const facilityKey = showFacility ? `${trailer.facility?.id || 'unknown'}:` : '';

      // Determine facility zone key and name based on location type
      if (locationType === 'YardSpotObject' && location.facility_zone) {
        facilityZoneKey = location.facility_zone.uuid;
        facilityZoneName = location.facility_zone.name;
      } else if (locationType === 'FacilityZoneObject') {
        facilityZoneKey = location.uuid || 'unknown';
        facilityZoneName = location.name;
      } else if (locationType === 'DockObject') {
        facilityZoneKey = 'warehouse';
        facilityZoneName = 'Warehouse';
      } else {
        facilityZoneKey = location.uuid || 'other';
        facilityZoneName = location.name || 'Other';
      }

      // 'warehouse' and fallback keys repeat across facilities, so scope keys by facility in a campus view.
      facilityZoneKey = `${facilityKey}${facilityZoneKey}`;
      facilityZoneName = formatCampusLocation(facilityZoneName, trailer.facility, showFacility);

      if (!groups[locationType]) {
        groups[locationType] = {};
      }
      if (!groups[locationType][facilityZoneKey]) {
        groups[locationType][facilityZoneKey] = {
          facilityZoneName,
          trailers: []
        };
      }
      
      groups[locationType][facilityZoneKey].trailers.push(trailer);
    });

    // Sort trailers within each group
    Object.keys(groups).forEach(locationType => {
      Object.keys(groups[locationType]).forEach(facilityZoneKey => {
        groups[locationType][facilityZoneKey].trailers.sort((a, b) => {
          // First, try to sort by location name (dock door, yard spot) if they're in the same type of location
          if (locationType === 'DockObject' || locationType === 'YardSpotObject') {
            const locationNameA = a.current_location?.name || '';
            const locationNameB = b.current_location?.name || '';
            
            if (locationNameA && locationNameB && locationNameA !== locationNameB) {
              // Extract numeric parts from names like "D35" → 35, "A15" → 15
              const numA = parseInt(locationNameA.replace(/[^0-9]/g, ''), 10);
              const numB = parseInt(locationNameB.replace(/[^0-9]/g, ''), 10);
              
              if (!isNaN(numA) && !isNaN(numB)) {
                // Both have numeric parts, compare numerically (D1, D2, D10 instead of D1, D10, D2)
                return numA - numB;
              }
              
              // At least one doesn't have numeric parts, use intelligent string comparison
              const comparison = locationNameA.localeCompare(locationNameB, undefined, { 
                numeric: true, 
                sensitivity: 'base' 
              });
              
              if (comparison !== 0) {
                return comparison;
              }
            }
          }
          
          // If location names are the same or not available, fall back to lineage created_at descending (newest first)
          const dateA = lineageCheckIn(a.active_lineage);
          const dateB = lineageCheckIn(b.active_lineage);
          
          // Handle cases where created_at might be missing
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1; // Put trailers without date at the end
          if (!dateB) return -1; // Put trailers without date at the end
          
          // Sort by date descending (newest first)
          return new Date(dateB).getTime() - new Date(dateA).getTime();
        });
      });
    });

    return groups;
  };

  // Toggle a filter tag in the search term (EMPTY, LOADED, OOS)
  // The status filter chips. "IN USE" is two words, so the search term is
  // tokenized with that phrase kept whole (as "in_use") and matched against
  // the status name with its space restored.
  const STATUS_TAGS = ['EMPTY', 'IN USE', 'LOADED'];
  const tagToken = (tag: string) => tag.toUpperCase().replace(/\s+/g, '_');
  const tokenizeSearch = (term: string) =>
    term.replace(/\bin\s+use\b/gi, 'IN_USE').split(/\s+/).filter(w => w.length > 0);

  const toggleSearchTag = (tag: string) => {
    const upperTag = tagToken(tag);
    const words = tokenizeSearch(searchTerm);
    const tagIndex = words.findIndex(w => w.toUpperCase() === upperTag);

    if (tagIndex !== -1) {
      // Remove the tag
      words.splice(tagIndex, 1);
    } else {
      // The status tags are mutually exclusive
      if (STATUS_TAGS.map(tagToken).includes(upperTag)) {
        STATUS_TAGS.map(tagToken).filter(t => t !== upperTag).forEach(other => {
          const idx = words.findIndex(w => w.toUpperCase() === other);
          if (idx !== -1) words.splice(idx, 1);
        });
      }
      words.push(upperTag);
    }
    setSearchTerm(words.map(w => (w.toUpperCase() === 'IN_USE' ? 'IN USE' : w)).join(' '));
  };

  const searchHasTag = (tag: string) => {
    const words = tokenizeSearch(searchTerm);
    return words.some(w => w.toUpperCase() === tagToken(tag));
  };

  // Filter trailers based on search term with exclusive multi-word search
  const getFilteredTrailers = (trailers: Trailer[], searchTerm: string) => {
    if (!searchTerm.trim()) return trailers;

    // Split search term into individual words and filter out empty strings
    // ("in use" stays one word so it matches the IN USE status, not every "in")
    const searchWords = tokenizeSearch(searchTerm.toLowerCase().trim()).map(word =>
      word.toLowerCase() === 'in_use' ? 'in use' : word.toLowerCase()
    );

    // Check if OOS is a search token - handle it specially since it's a data flag, not in searchable text
    const hasOosFilter = searchWords.includes('oos');
    const remainingWords = searchWords.filter(word => word !== 'oos');

    return trailers.filter(trailer => {
      // If OOS filter is active, trailer must be OOS
      if (hasOosFilter && !trailer.data?.oos) return false;

      // Combine all searchable text for this trailer
      const searchableText = [
        trailer.trailer_number,
        trailer.carrier?.name || '',
        trailer.status?.name || '',
        trailer.active_lineage?.trailer_state?.shipment?.reference_number || '',
        trailer.current_location?.name || '',
        trailer.current_location?.__typename === 'YardSpotObject' ? trailer.current_location?.facility_zone?.name || '' : '',
        trailer.active_lineage?.notes || ''
      ].join(' ').toLowerCase();

      // Check that ALL remaining search words are found in the searchable text (exclusive AND)
      return remainingWords.every(word => searchableText.includes(word));
    });
  };

  const filteredTrailers = getFilteredTrailers(trailers, searchTerm);
  const groupedTrailers = groupTrailersByLocation(filteredTrailers);

  // Calculate total count from grouped trailers to ensure consistency
  const totalDisplayedTrailers = Object.keys(groupedTrailers).reduce((total, locationType) => {
    return total + Object.keys(groupedTrailers[locationType]).reduce((subtotal, facilityZoneKey) => {
      return subtotal + groupedTrailers[locationType][facilityZoneKey].trailers.length;
    }, 0);
  }, 0);

  const getLocationTypeDisplayName = (locationType: string) => {
    switch (locationType) {
      case 'YardSpotObject':
        return 'Yard Spots';
      case 'DockObject':
        return 'Dock Doors';
      case 'FacilityZoneObject':
        return 'Facility Zones';
      case 'Unknown':
        return 'Unknown Location';
      default:
        return locationType.replace('Object', '');
    }
  };

  // Navigation helpers
  const scrollToGroup = (locationType: string, facilityZoneKey: string) => {
    const groupKey = `${locationType}-${facilityZoneKey}`;
    const element = groupRefs.current[groupKey];
    if (element) {
      // Add a small offset to account for sticky header
      const yOffset = -80; // Adjust this value as needed
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
      
      // Hide navigation on android mode or smaller screens (tablets in landscape)
      if (isMobile || isTabletOrMobile) {
        setShowNavigation(false);
      }
    }
  };

  const toggleNavigation = () => {
    setShowNavigation(!showNavigation);
  };

  // Jump to top function
  const jumpToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to fetch and show task details from a lock
  const handleShowTaskFromLock = async (lock: any) => {
    if (!lock.task_uuid) {
      console.error('No task UUID in lock object');
      return;
    }

    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // GraphQL query to fetch task by UUID
      const taskQuery = `
        query GetTask($taskUuid: String!) {
          task(uuid: $taskUuid) {
            uuid
            data
            task_type {
              uuid
              type
            }
            task_status {
              uuid
              status
            }
            from_location
            to_location
            trailer {
              uuid
              trailer_number
            }
            driver {
              id
              firstName
              lastName
            }
            created_at
            updated_at
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: taskQuery,
          variables: { taskUuid: lock.task_uuid }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        console.error('Failed to fetch task:', result.errors);
        // If task fetch fails, create a fallback task object from the lock
        const fallbackTask = {
          uuid: lock.task_uuid,
          task_status: { status: 'ACTIVE' },
          task_type: { type: lock.operation_type || 'Movement Task' },
          from_location: `${lock.model_name}: ${lock.resource?.name || lock.resource?.trailerNumber || lock.resource_uuid}`,
          to_location: 'Unknown',
          trailer: lock.resource?.trailerNumber ? { trailer_number: lock.resource.trailerNumber } : null,
          created_at: lock.created_at,
          data: {
            reason: lock.reason,
            operationType: lock.operation_type
          }
        };
        setSelectedTaskForDetails(fallbackTask);
        setShowTaskDetailsModal(true);
        return;
      }

      if (result.data?.task) {
        setSelectedTaskForDetails(result.data.task);
        setShowTaskDetailsModal(true);
      } else {
        console.error('No task found with UUID:', lock.task_uuid);
      }
    } catch (error) {
      console.error('Error fetching task details:', error);
      // Show fallback task details from lock
      const fallbackTask = {
        uuid: lock.task_uuid,
        task_status: { status: 'ACTIVE' },
        task_type: { type: lock.operation_type || 'Movement Task' },
        from_location: `${lock.model_name}: ${lock.resource?.name || lock.resource?.trailerNumber || lock.resource_uuid}`,
        to_location: 'Unknown',
        trailer: lock.resource?.trailerNumber ? { trailer_number: lock.resource.trailerNumber } : null,
        created_at: lock.created_at,
        data: {
          reason: lock.reason,
          operationType: lock.operation_type
        }
      };
      setSelectedTaskForDetails(fallbackTask);
      setShowTaskDetailsModal(true);
    }
  };

  // Function to cancel a task
  const handleCancelTask = async (taskId: string) => {
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      const cancelTaskMutation = `
        mutation CancelTask($taskUuid: String!) {
          cancel_task(uuid: $taskUuid) {
            uuid
            task_status {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: cancelTaskMutation,
          variables: { taskUuid: taskId }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh data to update the UI
      fetchTrailersData();
    } catch (error) {
      console.error('Failed to cancel task:', error);
      throw error;
    }
  };

  // Function to complete a task
  const handleCompleteTask = async (taskId: string) => {
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      const completeTaskMutation = `
        mutation CompleteTask($taskUuid: String!) {
          complete_task(uuid: $taskUuid) {
            uuid
            task_status {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: completeTaskMutation,
          variables: { taskUuid: taskId }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh data to update the UI
      fetchTrailersData();
    } catch (error) {
      console.error('Failed to complete task:', error);
      throw error;
    }
  };

  // Function to complete a movement task
  const handleCompleteMovementTask = async (taskId: string) => {
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      const completeMovementTaskMutation = `
        mutation CompleteMovementTask($input: CompleteMovementTaskInput!) {
          completeMovementTask(input: $input) {
            uuid
            taskStatus {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: completeMovementTaskMutation,
          variables: { 
            input: { 
              taskUuid: taskId 
            } 
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh data to update the UI
      fetchTrailersData();
    } catch (error) {
      console.error('Failed to complete movement task:', error);
      throw error;
    }
  };

  // Helper function to find locks affecting a specific trailer
  const getLocksForTrailer = (trailerUuid: string) => {
    return activeLocks.filter((lock: any) => {
      const lockTrailerUuid = lock.resource_uuid || lock.resource?.uuid;
      return lockTrailerUuid === trailerUuid;
    });
  };

  // Clear all locks for a trailer
  const handleClearLock = async (locks: any[]) => {
    if (locks.length === 0) return;
    
    setClearingLock(true);
    try {
      const authToken = getAuthToken();

      if (!authToken) {
        throw new Error('Authentication required');
      }

      const clearLockMutation = `
        mutation ReleaseTaskLocks($taskUuid: String!) {
          releaseTaskLocks(taskUuid: $taskUuid) {
            success
            message
          }
        }
      `;

      // Clear all locks in sequence
      for (const lock of locks) {
        const taskUuid = lock.task_uuid || lock.resource_uuid;
        
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: clearLockMutation,
            variables: { taskUuid }
          })
        });

        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0].message);
        }
      }

      alert(`${locks.length} lock(s) cleared successfully!`);
      
      // Reload the page to refresh all data
      window.location.reload();
    } catch (error: any) {
      console.error('Error clearing lock:', error);
      alert(`Failed to clear lock: ${error.message}`);
      setClearingLock(false);
    }
  };

  // Loading state for initial facilities fetch
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading facilities...</Typography>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>{error}</Typography>
        <IconButton onClick={handleRefresh}>
          <Refresh />
        </IconButton>
      </div>
    );
  }

  return (
    <Box sx={{
      '& .MuiTypography-root': {
        fontSize: (theme) => `calc(${theme.typography.fontSize}px * 1.5) !important`
      },
      '& .MuiChip-root': {
        fontSize: '1.2rem !important'
      },
      '& .MuiButton-root': {
        fontSize: '1.3rem !important'
      },
      '& .MuiTableCell-root': {
        fontSize: '1.3rem !important'
      },
      '& .MuiInputBase-root': {
        fontSize: '1.3rem !important'
      }
    }}>
      <div
        className="p-2 sm:p-5"
        style={{
          paddingRight: showNavigation && !isMobile ? '320px' : undefined,
          transition: 'padding-right 0.3s ease-in-out'
        }}
      >
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        
        
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, sm: 2 }, 
          alignItems: 'center', 
          flexWrap: 'wrap',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Facility Selector - Only show when multiple facilities */}
            {facilities.length > 1 && (
              <FormControl 
                variant="outlined" 
                size="small" 
                sx={{ 
                  minWidth: { xs: 120, sm: 200 },
                  maxWidth: { xs: 150, sm: 'none' }
                }}
              >
                <InputLabel>Campus</InputLabel>
                <Select
                  value={selectedFacility?.id || ''}
                  onChange={handleFacilityChange}
                  label="Campus"
                >
                  {facilities.map(facility => (
                    <MenuItem key={facility.id} value={facility.id}>
                      <Typography variant="body2" noWrap>
                        ({facility.id}) {facilityDisplayName(facility)}
                      </Typography>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Which facilities of the campus to show - only in a multi-facility campus */}
            <CampusFacilityMultiSelect
              facilities={campusFacilities.map(f => ({ id: String(f.id), name: facilityDisplayName(f) }))}
              value={selectedCampusFacilityIds}
              onChange={setSelectedCampusFacilityIds}
              sx={{ minWidth: { xs: 150, sm: 220 }, maxWidth: { xs: 200, sm: 320 } }}
            />

            {/* Search Field */}
            <TrailerSearchField value={searchTerm} onChange={setSearchTerm} />

            {/* Filter Tag Buttons */}
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Chip
                label="EMPTY"
                size="small"
                variant={searchHasTag('EMPTY') ? 'filled' : 'outlined'}
                color={searchHasTag('EMPTY') ? 'warning' : 'default'}
                onClick={() => toggleSearchTag('EMPTY')}
                sx={{ cursor: 'pointer', fontWeight: searchHasTag('EMPTY') ? 700 : 400 }}
              />
              <Chip
                label="IN USE"
                size="small"
                variant={searchHasTag('IN USE') ? 'filled' : 'outlined'}
                color={searchHasTag('IN USE') ? 'info' : 'default'}
                onClick={() => toggleSearchTag('IN USE')}
                sx={{ cursor: 'pointer', fontWeight: searchHasTag('IN USE') ? 700 : 400 }}
              />
              <Chip
                label="LOADED"
                size="small"
                variant={searchHasTag('LOADED') ? 'filled' : 'outlined'}
                color={searchHasTag('LOADED') ? 'primary' : 'default'}
                onClick={() => toggleSearchTag('LOADED')}
                sx={{ cursor: 'pointer', fontWeight: searchHasTag('LOADED') ? 700 : 400 }}
              />
              <Chip
                label="OOS"
                size="small"
                variant={searchHasTag('OOS') ? 'filled' : 'outlined'}
                color={searchHasTag('OOS') ? 'error' : 'default'}
                onClick={() => toggleSearchTag('OOS')}
                sx={{ cursor: 'pointer', fontWeight: searchHasTag('OOS') ? 700 : 400 }}
              />
            </Box>

            {/* Refresh Button */}
            <IconButton
              onClick={handleRefresh}
              disabled={isLoadingTable}
              color="primary"
              size="small"
            >
              {isLoadingTable ? <CircularProgress size={20} /> : <Refresh />}
            </IconButton>
          </Box>

          {/* Trailer Count and Add Button */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {/* Search Results Info */}
            {searchTerm && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {totalDisplayedTrailers === 0
                  ? `No trailers found matching "${searchTerm}"`
                  : `Showing ${totalDisplayedTrailers} trailer${totalDisplayedTrailers === 1 ? '' : 's'} matching "${searchTerm}"`
                }
              </Typography>
            )}
            <Chip 
              label={searchTerm ? `${totalDisplayedTrailers} of ${trailers.length} trailers` : `${trailers.length} trailers`}
              color="primary" 
              size="small"
              sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<Add />}
              onClick={() => {
                setIsAddTrailerModalOpen(true);
                fetchAvailableLocations(); // Fetch locations when opening the modal
              }}
              sx={{ 
                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                minWidth: 'auto',
                px: { xs: 1, sm: 2 }
              }}
            >
              Add Trailer
            </Button>

            {/* Report Generator */}
            {selectedFacility && user?.id && (() => {
              try {
                const authToken = getAuthToken() || '';

                if (!authToken) return null;

                const decoded: any = jwtDecode(authToken);
                const publicUserId = decoded.user_id;

                if (!publicUserId) return null;

                return (
                  <ReportButton
                    userId={publicUserId}
                    jwtToken={authToken}
                    facilityId={selectedFacility.id}
                    websocketUrl={REPORTS_WEBSOCKET_URL}
                  />
                );
              } catch (error) {
                console.error('Failed to decode JWT:', error);
                return null;
              }
            })()}

        </Box>
        
        </Box>
      </Box>

      {/* Table */}
      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxWidth: '100vw' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: { xs: 100, sm: 150 }, p: { xs: 1, sm: 2 } }}>
                  <Typography variant="body2" fontWeight="bold">
                    Trailer / SCAC
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: { xs: 80, sm: 120 }, p: { xs: 1, sm: 2 } }}>
                  <Typography variant="body2" fontWeight="bold">
                    Status
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: { xs: 80, sm: 120 }, p: { xs: 1, sm: 2 } }}>
                  <Typography variant="body2" fontWeight="bold">
                    Shipment Ref
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: { xs: 80, sm: 120 }, p: { xs: 1, sm: 2 } }}>
                  <Typography variant="body2" fontWeight="bold">
                    Check-In Time
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: { xs: 80, sm: 120 }, p: { xs: 1, sm: 2 } }}>
                  <Typography variant="body2" fontWeight="bold">
                    Current Location
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: { xs: 100, sm: 160 }, p: { xs: 1, sm: 2 } }}>
                  <Typography variant="body2" fontWeight="bold">
                    Notes
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: { xs: 80, sm: 120 }, p: { xs: 1, sm: 2 } }}>
                  <Typography variant="body2" fontWeight="bold">
                    Actions
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingTable ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : totalDisplayedTrailers === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      {searchTerm ? `No trailers found matching "${searchTerm}"` : 'No trailers found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                Object.keys(groupedTrailers).sort((a, b) => {
                  // Sort so that "Unknown" appears at the bottom
                  if (a === 'Unknown' && b !== 'Unknown') return 1;
                  if (b === 'Unknown' && a !== 'Unknown') return -1;
                  return a.localeCompare(b);
                }).map(locationType => (
                  Object.keys(groupedTrailers[locationType]).map(facilityZoneKey => {
                    const group = groupedTrailers[locationType][facilityZoneKey];
                    return [
                      // Group header row
                      <TableRow 
                        key={`${locationType}-${facilityZoneKey}-header`}
                        ref={(el) => {
                          groupRefs.current[`${locationType}-${facilityZoneKey}`] = el;
                        }}
                      >
                        <TableCell 
                          colSpan={7} 
                          sx={{ 
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            p: { xs: 1, sm: 2 },
                            fontWeight: 'bold'
                          }}
                        >
                          <Typography variant="body2" fontWeight="bold">
                            {getLocationTypeDisplayName(locationType)} - {group.facilityZoneName} ({group.trailers.length} trailers)
                          </Typography>
                        </TableCell>
                      </TableRow>,
                      // Trailer rows for this group
                      ...group.trailers.map(trailer => (
                        <TableRow 
                          key={trailer.uuid} 
                          hover
                          sx={{ 
                            cursor: trailer.isLocked ? 'pointer' : 'default',
                            backgroundColor: trailer.isLocked ? 'action.hover' : 'inherit'
                          }}
                          onClick={trailer.isLocked ? () => {
                            const locks = getLocksForTrailer(trailer.uuid);
                            if (locks.length > 0) {
                              handleShowTaskFromLock(locks[0]);
                            }
                          } : undefined}
                        >
                          <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                            <Typography 
                              variant="body2" 
                              fontWeight="medium"
                              sx={{ 
                                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                opacity: trailer.isLocked ? 0.7 : 1
                              }}
                            >
                              {trailer.trailer_number}
                              {trailer.isLocked && (
                                <Chip 
                                  label="LOCKED" 
                                  color="warning" 
                                  size="small"
                                  sx={{ ml: 1, fontSize: '0.6rem' }}
                                />
                              )}
                            </Typography>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ 
                                display: 'block',
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                opacity: trailer.isLocked ? 0.7 : 1
                              }}
                            >
                              {trailer.carrier?.name || 'No SCAC'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  opacity: trailer.isLocked ? 0.7 : 1
                                }}
                              >
                                {trailer.status?.name || 'Unknown'}
                              </Typography>
                              {trailer.data?.oos && (
                                <Chip
                                  label="OOS"
                                  color="error"
                                  size="small"
                                  sx={{
                                    fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                    height: { xs: 18, sm: 20 },
                                    '& .MuiChip-label': {
                                      padding: { xs: '0 4px', sm: '0 6px' }
                                    }
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                            <Typography 
                              variant="body2"
                              sx={{ 
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                opacity: trailer.isLocked ? 0.7 : 1
                              }}
                            >
                              {trailer.active_lineage?.trailer_state?.shipment?.reference_number || 'No Shipment'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                            <Typography 
                              variant="body2"
                              sx={{ 
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                opacity: trailer.isLocked ? 0.7 : 1
                              }}
                            >
                              {formatCheckInTime(lineageCheckIn(trailer.active_lineage))}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                            <Typography 
                              variant="body2"
                              sx={{ 
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                opacity: trailer.isLocked ? 0.7 : 1
                              }}
                            >
                              {formatCurrentLocation(trailer.current_location)}
                              <FacilityBadge facility={trailer.facility} show={showFacility} className="ml-1" />
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 2 }, maxWidth: 240 }}>
                            <Tooltip title={trailer.active_lineage?.notes || ''}>
                              <Typography
                                variant="body2"
                                noWrap
                                color={trailer.active_lineage?.notes ? 'text.primary' : 'text.secondary'}
                                sx={{
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  opacity: trailer.isLocked ? 0.7 : 1
                                }}
                              >
                                {trailer.active_lineage?.notes || '—'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ p: { xs: 1, sm: 2 } }}>
                            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<Edit sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditTrailer(trailer);
                                }}
                                disabled={trailer.isLocked}
                                sx={{ 
                                  fontSize: { xs: '0.65rem', sm: '0.8rem' },
                                  minWidth: { xs: 45, sm: 'auto' },
                                  px: { xs: 1, sm: 2 }
                                }}
                              >
                                {isMobile ? 'Edit' : 'Edit'}
                              </Button>
                              
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<DirectionsCar sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMoveTask(trailer);
                                }}
                                disabled={trailer.isLocked || trailer.data?.oos}
                                sx={{ 
                                  fontSize: { xs: '0.65rem', sm: '0.8rem' },
                                  minWidth: { xs: 45, sm: 'auto' },
                                  px: { xs: 1, sm: 2 }
                                }}
                              >
                                {isMobile ? 'Move' : 'Move'}
                              </Button>
                              
                              {/* Only show Add Inspection button if trailer is NOT at a dock door */}
                              {trailer.current_location?.__typename !== 'DockObject' && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  startIcon={<Assignment sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddInspectionTask(trailer);
                                  }}
                                  disabled={trailer.isLocked}
                                  sx={{ 
                                    fontSize: { xs: '0.65rem', sm: '0.8rem' },
                                    minWidth: { xs: 45, sm: 'auto' },
                                    px: { xs: 1, sm: 2 }
                                  }}
                                >
                                  {isMobile ? 'Inspect' : 'Add Inspection'}
                                </Button>
                              )}
                              
                              {/* Show Clear Lock button only when trailer is locked */}
                              {trailer.isLocked && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  startIcon={clearingLock ? <CircularProgress size={16} /> : <LockOpen sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const locks = getLocksForTrailer(trailer.uuid);
                                    if (locks.length > 0) {
                                      handleClearLock(locks);
                                    }
                                  }}
                                  disabled={clearingLock}
                                  sx={{ 
                                    fontSize: { xs: '0.65rem', sm: '0.8rem' },
                                    minWidth: { xs: 45, sm: 'auto' },
                                    px: { xs: 1, sm: 2 }
                                  }}
                                >
                                  {isMobile ? 'Clear' : 'Clear Lock'}
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ];
                  }).flat()
                )).flat()
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Floating Action Buttons */}
      
      {/* Jump to Top Button - Show when scrolled down */}
      {showJumpToTop && (
        <Fab
          onClick={jumpToTop}
          sx={{
            position: 'fixed',
            top: { xs: 80, sm: 100 },
            right: { xs: 16, sm: showNavigation ? 320 : 24 },
            zIndex: 1000,
            transition: 'all 0.3s ease-in-out',
            backgroundColor: '#2dc3e8',
            color: 'white',
            boxShadow: '0 4px 20px rgba(45, 195, 232, 0.4)',
            border: '2px solid white',
            '&:hover': {
              backgroundColor: '#26b3d1',
              transform: 'scale(1.1)',
              boxShadow: '0 6px 25px rgba(45, 195, 232, 0.6)'
            }
          }}
        >
          <KeyboardArrowUp />
        </Fab>
      )}

      {/* Quick Navigation - Show when there are trailers and multiple groups */}
      {trailers.length > 0 && Object.keys(groupedTrailers).length > 0 && (
        <>
          {/* Navigation Toggle Fab */}
        <Fab
          onClick={toggleNavigation}
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: showNavigation ? 320 : 24 },
            zIndex: 1000,
            transition: 'all 0.3s ease-in-out',
            backgroundColor: '#ff6b35',
            color: 'white',
            boxShadow: '0 4px 20px rgba(255, 107, 53, 0.4)',
            border: '2px solid white',
            '&:hover': {
              backgroundColor: '#e55a2b',
              transform: 'scale(1.1)',
              boxShadow: '0 6px 25px rgba(255, 107, 53, 0.6)'
            }
          }}
                  >
            <Box sx={{ textAlign: 'center', lineHeight: 1 }}>
              <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem', display: 'block' }}>
                Jump
              </Typography>
              <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem', display: 'block' }}>
                To
              </Typography>
            </Box>
          </Fab>

          {/* Navigation Panel */}
          <Paper
            elevation={3}
            sx={{
              position: 'fixed',
              top: { xs: 0, md: 100 },
              right: { xs: showNavigation ? 0 : '-100%', md: showNavigation ? 16 : -300 },
              width: { xs: '100%', md: 300 },
              height: { xs: '100vh', md: 'auto' },
              maxHeight: { md: 'calc(100vh - 120px)' },
              zIndex: 999,
              transition: 'right 0.3s ease-in-out',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box sx={{ 
              p: 2, 
              backgroundColor: 'primary.main', 
              color: 'primary.contrastText',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography variant="h6" fontWeight="bold">
                Jump to Section
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {searchTerm ? `${totalDisplayedTrailers} trailers` : `${Object.keys(groupedTrailers).length} location types`}
              </Typography>
              <IconButton
                size="small"
                onClick={toggleNavigation}
                sx={{ 
                  color: 'primary.contrastText',
                  display: { xs: 'block', md: 'none' }
                }}
              >
                <KeyboardArrowUp />
              </IconButton>
            </Box>
            
            <Box sx={{ maxHeight: { xs: 'calc(100vh - 80px)', md: '60vh' }, overflow: 'auto' }}>
              <List dense>
                {Object.keys(groupedTrailers).sort((a, b) => {
                  // Sort so that "Unknown" appears at the bottom
                  if (a === 'Unknown' && b !== 'Unknown') return 1;
                  if (b === 'Unknown' && a !== 'Unknown') return -1;
                  return a.localeCompare(b);
                }).map(locationType => {
                  const locationTypeGroups = groupedTrailers[locationType];
                  const totalTrailers = Object.values(locationTypeGroups).reduce((sum, group) => sum + group.trailers.length, 0);
                  
                  return (
                    <Box key={`nav-${locationType}`}>
                      {/* Location Type Header */}
                      <ListItem sx={{ backgroundColor: 'grey.100', py: 1 }}>
                        <ListItemText
                          primary={
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {getLocationTypeDisplayName(locationType)} ({totalTrailers} trailers)
                            </Typography>
                          }
                        />
                      </ListItem>
                      
                      {/* Facility Zone Subgroups */}
                      {Object.keys(locationTypeGroups).map(facilityZoneKey => {
                        const group = locationTypeGroups[facilityZoneKey];
                        return (
                          <ListItem key={`nav-${locationType}-${facilityZoneKey}`} disablePadding>
                            <ListItemButton
                              onClick={() => scrollToGroup(locationType, facilityZoneKey)}
                              sx={{
                                pl: 4,
                                pr: 2,
                                py: 0.5,
                                '&:hover': {
                                  backgroundColor: 'action.hover'
                                },
                                borderRadius: 1
                              }}
                            >
                              <ListItemText
                                primary={
                                  <Typography variant="body2" fontWeight="medium">
                                    {group.facilityZoneName}
                                  </Typography>
                                }
                                secondary={
                                  <Typography variant="caption" color="text.secondary">
                                    {group.trailers.length} trailers
                                  </Typography>
                                }
                              />
                            </ListItemButton>
                          </ListItem>
                        );
                      })}
                      
                      {/* Divider between location types */}
                      <Divider sx={{ my: 1 }} />
                    </Box>
                  );
                })}
              </List>
            </Box>
          </Paper>

          {/* Mobile Overlay */}
          {showNavigation && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 998,
                display: { xs: 'block', md: 'none' }
              }}
              onClick={() => setShowNavigation(false)}
            />
          )}
        </>
      )}

      {/* Edit Trailer Modal */}
      <Dialog
        open={isEditTrailerModalOpen}
        onClose={() => setIsEditTrailerModalOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 0, sm: 2 },
            maxHeight: { xs: '100vh', sm: 'calc(100vh - 64px)' }
          }
        }}
      >
        <DialogTitle>Edit Trailer</DialogTitle>
        <DialogContent sx={{ overflow: 'visible' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {/* Trailer Information Section */}
            <Box 
              sx={{ 
                p: 2, 
                border: 1, 
                borderColor: 'divider', 
                borderRadius: 2,
                backgroundColor: 'background.default'
              }}
            >
              <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                Trailer Information
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Check-In Time:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatCheckInTime(lineageCheckIn(selectedTrailer?.active_lineage))}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Current Location:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatCurrentLocation(selectedTrailer?.current_location)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <TextField
              fullWidth
              label="Trailer Number"
              value={newTrailerNumber}
              inputProps={{ maxLength: 27 }}
              onChange={(e) => {
                // Enforce alphanumeric only, force uppercase, and strictly enforce max length of 27
                let value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                if (value.length > 27) {
                  value = value.substring(0, 27);
                }
                setNewTrailerNumber(value);
              }}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="SCAC (Carrier Name)"
              value={newScacValue}
              inputProps={{ maxLength: 10 }}
              onChange={(e) => {
                // Enforce alphanumeric only, force uppercase, and strictly enforce max length of 10
                let value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                if (value.length > 10) {
                  value = value.substring(0, 10);
                }
                setNewScacValue(value);
              }}
              variant="outlined"
            />

            {/* Status Toggle Section */}
            {selectedTrailer?.active_lineage?.uuid && (
              <Box 
                sx={{ 
                  mt: 2, 
                  p: 2, 
                  border: 1, 
                  borderColor: 'divider', 
                  borderRadius: 2,
                  backgroundColor: 'background.default'
                }}
              >
                <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 2 }}>
                  Trailer Status
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                   <Typography variant="body2">
                     Current Status: <strong>{selectedTrailer?.status?.name || 'Unknown'}</strong>
                   </Typography>
                   <FormControlLabel
                     control={
                       <Checkbox
                         checked={newStatusLoaded}
                         onChange={(e) => {
                           setNewStatusLoaded(e.target.checked);
                         }}
                       />
                     }
                     label={
                       <Typography variant="body2">
                         Loaded (uncheck to set Empty)
                       </Typography>
                     }
                   />
                   <FormControlLabel
                     control={
                       <Checkbox
                         checked={newOutOfService}
                         onChange={(e) => {
                           const newValue = e.target.checked;

                           // Only show confirmation if the value is different from original
                           if (newValue !== originalOutOfService) {
                             const confirmed = window.confirm('Are you sure?');
                             if (confirmed) {
                               setNewOutOfService(newValue);
                             }
                           } else {
                             setNewOutOfService(newValue);
                           }
                         }}
                       />
                     }
                     label={
                       <Typography variant="body2">
                         Out of Service
                       </Typography>
                     }
                   />
                 </Box>
                
                {/* Shipment Relationship Section */}
                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Shipment Reference:
                  </Typography>
                  {shipmentReferenceError && (
                    <Box sx={{ mb: 1, p: 1, backgroundColor: 'error.light', borderRadius: 1 }}>
                      <Typography variant="body2" color="">
                        {shipmentReferenceError}
                      </Typography>
                    </Box>
                  )}
                  <TextField
                    fullWidth
                    size="small"
                    value={newShipmentReference}
                    onChange={(e) => {
                      setNewShipmentReference(e.target.value);
                      setShipmentReferenceTouched(true);
                      if (shipmentReferenceError) {
                        setShipmentReferenceError(''); // Clear error when user starts typing
                      }
                    }}
                    placeholder="Enter shipment reference number"
                    variant="outlined"
                    error={!!shipmentReferenceError}
                  />
                  {selectedTrailer?.active_lineage?.trailer_state?.shipment?.reference_number && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => setShowRemoveShipmentConfirm(true)}
                      disabled={!isRemoveShipmentButtonEnabled(selectedTrailer)}
                      sx={{
                        fontSize: '0.75rem',
                        opacity: !isRemoveShipmentButtonEnabled(selectedTrailer) ? 0.5 : 1,
                        mt: 1
                      }}
                    >
                      Remove Shipment Relationship
                    </Button>
                  )}
                </Box>
              </Box>
            )}

            {/* Trailer Notes Section */}
            <Box 
              sx={{ 
                mt: 3, 
                p: 2, 
                border: 2, 
                borderColor: 'primary.main', 
                borderRadius: 2,
                backgroundColor: 'background.default'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  Trailer Notes
                </Typography>
                {!isEditingTrailerNotes && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<Edit />}
                    onClick={() => setIsEditingTrailerNotes(true)}
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {selectedTrailer?.active_lineage?.notes ? 'Edit Notes' : 'Add Notes'}
                  </Button>
                )}
              </Box>
              
              {isEditingTrailerNotes ? (
                <TrailerNotesEditor
                  initialNotes={selectedTrailer?.active_lineage?.notes || ''}
                  saving={savingTrailerNotes}
                  onSave={handleSaveTrailerNotes}
                  onCancel={() => setIsEditingTrailerNotes(false)}
                />
              ) : (
                <Box sx={{ 
                  p: 2, 
                  border: 1, 
                  borderColor: 'divider', 
                  borderRadius: 1, 
                  backgroundColor: selectedTrailer?.active_lineage?.notes ? 'background.paper' : 'grey.50',
                  minHeight: 60
                }}>
                  {selectedTrailer?.active_lineage?.notes ? (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedTrailer.active_lineage.notes}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No trailer notes added
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditTrailerModalOpen(false)} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateTrailer}
            variant="contained"
            disabled={!newTrailerNumber.trim() || !newScacValue.trim() || isUpdatingTrailer}
            startIcon={isUpdatingTrailer ? <CircularProgress size={16} /> : <Save />}
          >
            {isUpdatingTrailer ? 'Updating...' : 'Update Trailer'}
          </Button>
        </DialogActions>
      </Dialog>

      {shipmentChoiceDialog}
      {createdByFacilityDialog}

      {/* Move Task Modal - Using Shared Component */}
            <TrailerMoveModal
              open={isMoveTaskModalOpen && selectedTrailer !== null}
              onClose={() => setIsMoveTaskModalOpen(false)}
              onSuccess={handleMoveTaskSuccess}
              onError={handleMoveTaskError}
              trailer={selectedTrailer || {}}
              title="Create Move Task"
              facilityId={moveSourceFacilityId || "1"}
              destinationFacilities={campusDestinations.facilities}
              availableDockDoors={campusDestinations.dockDoors}
              availableYardSpots={campusDestinations.yardSpots}
              availableFacilityZones={campusDestinations.facilityZones}
              zones={campusDestinations.facilityZones.map(zone => ({
                id: zone.facility_zone_uuid,
                name: zone.facility_zone_name,
                color: zoneColor(zone),
                zone_type: zone.zone_type,
                facilityId: zone.facilityId
              }))}
              moveType="any"
              allowLeaveFacility={true}
              requireShipmentValidation={false}
            />

      {/* Add Trailer Modal */}
      <AddTrailerModal
        open={isAddTrailerModalOpen}
        onClose={() => setIsAddTrailerModalOpen(false)}
        onSuccess={(trailerData) => {
          setIsAddTrailerModalOpen(false);
          // Refresh the trailers list to show the new trailer
          handleRefresh();
          // Show simple success message
          setAddTrailerSuccessMessage(`Trailer ${trailerData.trailer?.trailerNumber} has been added successfully!`);
          setShowAddTrailerSuccessModal(true);
        }}
        onSafetyCheckCreated={(taskData) => {
          setAddTrailerSuccessMessage(prev => prev ? `${prev} Safety check created.` : 'Safety check created.');
        }}
        onError={(error) => {
          console.error('Failed to add trailer:', error);
          // Keep modal open to show error
        }}
        facilityId={selectedFacility?.id?.toString() || "1"}
        loadingLocations={isLoadingLocations}
        availableDockDoors={availableDockDoors}
        availableYardSpots={availableYardSpots}
        availableFacilityZones={availableFacilityZones}
        zones={(() => {
          // Extract unique zones from availableFacilityZones with proper color handling
          return availableFacilityZones.map(zone => ({
            id: zone.facility_zone_uuid,
            name: zone.facility_zone_name,
            color: zone.configuration?.color ||
                   zone.color || 
                   '#3B82F6' // Default blue color if no color is set
          }));
        })()}
      />

      {/* Add Inspection Task Modal */}
      <Dialog
        open={isAddInspectionModalOpen}
        onClose={() => setIsAddInspectionModalOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 0, sm: 2 },
            maxHeight: { xs: '100vh', sm: 'calc(100vh - 64px)' }
          }
        }}
      >
        <DialogTitle>Add Inspection Task</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2">
              Add safety inspection task for trailer: {selectedTrailer?.trailer_number}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Current location: {formatCurrentLocation(selectedTrailer?.current_location)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddInspectionModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateInspectionTask}
            variant="contained"
            disabled={isCreatingInspectionTask}
            startIcon={isCreatingInspectionTask ? <CircularProgress size={16} /> : <Add />}
          >
            {isCreatingInspectionTask ? 'Creating...' : 'Create Inspection Task'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Task Details Modal */}
      <TaskDetailsModal
        open={showTaskDetailsModal}
        onClose={() => setShowTaskDetailsModal(false)}
        task={selectedTaskForDetails}
        onCompleteTask={handleCompleteTask}
        onCancelTask={handleCancelTask}
        onCompleteMovementTask={handleCompleteMovementTask}
      />

      {/* Success Modal */}
      {showSuccessModal && createdTask && (
        <Dialog
          open={showSuccessModal}
          onClose={() => {
            setShowSuccessModal(false);
            setCreatedTask(null);
          }}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          sx={{
            '& .MuiDialog-paper': {
              margin: { xs: 0, sm: 2 },
              maxHeight: { xs: '100vh', sm: 'calc(100vh - 64px)' }
            }
          }}
        >
          <DialogTitle sx={{ backgroundColor: 'success.main', color: 'success.contrastText' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assignment />
              <Typography variant="h6" component="div">
                Task Created Successfully!
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'success.main' }}>
                {createdTask.task_type?.type === 'yard_check' ? 'Inspection' : 'Movement'} task has been created successfully!
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Task Type:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {createdTask.task_type?.type || 'Movement Task'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {createdTask.task_status?.status || 'PENDING'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Trailer:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {createdTask.trailer?.trailer_number || 'Unknown'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Task ID:</Typography>
                  <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                    {createdTask.uuid?.substring(0, 8)}...
                  </Typography>
                </Box>
              </Box>

              {createdTask.from_location && (
                <Box>
                  <Typography variant="body2" color="text.secondary">From Location:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {createdTask.from_location}
                  </Typography>
                </Box>
              )}

              {createdTask.to_location && (
                <Box>
                  <Typography variant="body2" color="text.secondary">To Location:</Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {createdTask.to_location}
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1, flexWrap: 'wrap' }}>
            <Button
              onClick={() => {
                setShowSuccessModal(false);
                setCreatedTask(null);
              }}
              variant="outlined"
              startIcon={<Cancel />}
            >
              Close
            </Button>
            
            <Button
              onClick={() => {
                // Store task data before closing the modal
                const taskToView = createdTask;
                
                // Close success modal first
                setShowSuccessModal(false);
                setCreatedTask(null);
                
                // Then open task details modal with a small delay to ensure clean transition
                setTimeout(() => {
                  setSelectedTaskForDetails(taskToView);
                  setShowTaskDetailsModal(true);
                }, 100);
              }}
              variant="contained"
              startIcon={<Launch />}
              color="primary"
            >
              View Task Details
            </Button>

            {/* Auto-complete tasks or inspection tasks can be completed immediately */}
            {(createdTask.task_type?.type === 'yard_check' || createdTask.data?.auto_complete) && (
              <Button
                onClick={async () => {
                  try {
                    if (createdTask.task_type?.type === 'yard_check') {
                      await handleCompleteTask(createdTask.uuid);
                    } else {
                      await handleCompleteMovementTask(createdTask.uuid);
                    }
                    setShowSuccessModal(false);
                    setCreatedTask(null);
                  } catch (error) {
                    console.error('Failed to complete task:', error);
                    setError('Failed to complete task');
                  }
                }}
                variant="contained"
                startIcon={<Assignment />}
                color="success"
              >
                Complete Task Now
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}

      {/* Add Trailer Success Modal */}
      <Dialog
        open={showAddTrailerSuccessModal}
        onClose={() => {
          setShowAddTrailerSuccessModal(false);
          setAddTrailerSuccessMessage('');
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 0, sm: 2 },
            maxHeight: { xs: '100vh', sm: 'calc(100vh - 64px)' }
          }
        }}
      >
        <DialogTitle sx={{ backgroundColor: 'success.main', color: 'success.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DirectionsCar />
            <Typography variant="h6" component="div">
              Trailer Added Successfully!
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1">
            {addTrailerSuccessMessage}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowAddTrailerSuccessModal(false);
              setAddTrailerSuccessMessage('');
            }}
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Shipment Confirmation Dialog */}
      <Dialog
        open={showRemoveShipmentConfirm}
        onClose={() => setShowRemoveShipmentConfirm(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Delete />
            Remove Shipment Relationship
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to remove the shipment relationship from trailer <strong>{selectedTrailer?.trailer_number}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current shipment: <strong>{selectedTrailer?.active_lineage?.trailer_state?.shipment?.reference_number}</strong>
          </Typography>
          <Typography variant="body2" color="error.main" sx={{ mt: 1, fontWeight: 'medium' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowRemoveShipmentConfirm(false)}
            disabled={isRemovingShipment}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveShipmentRelationship}
            variant="contained"
            color="error"
            disabled={isRemovingShipment}
            startIcon={isRemovingShipment ? <CircularProgress size={16} /> : <Delete />}
          >
            {isRemovingShipment ? 'Removing...' : 'Remove Relationship'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
    </Box>
  );
};

export default Trailers; 